<template>
	<div class="c-ir-events" v-if="events.length">
		<ul class="c-ir-events__list">
			<li
				class="c-ir-events__item"
				v-for="event in events"
				:key="event.startDate"
			>
				<div class="c-ir-events__top">
					<DsText
						html-element="h3"
						text-type="heading-lg"
						text-style="medium"
						><span v-html="event.title"></span></DsText
					>
					<div class="c-ir-events__location" v-if="event.location"><DsIcon icon-name="pin"></DsIcon> <DsText text-type="heading-lg" html-element="span">{{ event.location }}</DsText></div>
				</div>
				<time class="c-ir-events__date" :datetime="event.startDate" :title="event.displayDate">
					<DsText
						class="c-ir-events__day"
						html-element="span"
						text-type="heading-6xl"
						text-style="regular"
						>{{ getDay(event.startDate) }}</DsText
					>
					<DsText html-element="div" text-type="body-xs" text-style="medium" class="c-ir-events__month-year">
						{{ getMonth(event.startDate) }}<br/>
						{{ getYear(event.startDate) }}
					</DsText>
				</time>
			</li>
		</ul>
	</div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import { DsIcon, DsText } from '@coloplast/design-system';

interface Event {
	lang: string;
	displayDate: string;
	attachments: string[];
	title: string;
	description: string;
	type: string;
	location: string;
	url: string;
	urlDesc: string;
	dateType: number;
	startDate: string;
	endDate: string;
}

interface ApiResponse {
	events: Event[];
}

const now = () => new Date().toISOString().slice(0, 10).replace(/-/g, '');
const EVENTS_API = `https://tools.euroland.com/tools/fincalendar2/rest/json/?companycode=dk-colo&startdate=${now()}&sortorder=asc`;

const events = ref<Event[]>([]);

const getDay = (dateString: string): string =>
	new Intl.DateTimeFormat('en-GB', { day: '2-digit' }).format(
		new Date(dateString)
	);

const getMonth = (dateString: string): string =>
	new Intl.DateTimeFormat('en-GB', { month: 'short' }).format(
		new Date(dateString)
	);

const getYear = (dateString: string): string =>
	new Intl.DateTimeFormat('en-GB', { year: 'numeric' }).format(
		new Date(dateString)
	);

onMounted(async () => {
	try {
		const response = await fetch(EVENTS_API);
		if (!response.ok) throw new Error('Failed to fetch events');
		const data: ApiResponse = await response.json();

		events.value = Array.isArray(data.events)
			? data.events.slice(0, 3)
			: [];
	} catch (error) {
		console.error('Error fetching events:', error);
	}
});
</script>

<style lang="scss" scoped>
@import 'multisite/styles/settings/settings';
@import 'multisite/styles/tools/tools';

.c-ir-events {
	--card-min-height: 220px;
	--grid-cols: span 2;

	@media (min-width: ds-breakpoint('sm')) {
		--grid-cols: span 12;
	}

	@media (min-width: ds-breakpoint('lg')) {
		--card-min-height: 280px;
		--grid-cols: span 4;
	}

	margin-block-start: calc(#{ds-space(10) - #{ds-gap(6)}});

	&__list {
		@include ds-block-grid();
		list-style: none;
		margin: 0;
		padding: 0;
	}

	&__item {
		background-color: ds-color(bg, brand-subtle);
		display: flex;
		flex-direction: column;
		grid-column: var(--grid-cols);
		min-height: var(--card-min-height);
		padding: ds-space(8);
	}

	&__top {
		display: flex;
		flex-direction: column;
		flex-grow: 1;
		justify-content: space-between;
		padding-block-end: ds-gap(5);
	}

	&__date {
		align-items: center;
		border-block-start: 1px solid ds-color(border, default);
		display: flex;
		justify-content: space-between;
		padding-block-start: ds-space(8);
	}
}
</style>
